import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, authHeaders, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Store, Clock, Heart, Globe, Tag, Info, Loader2, CheckCircle2, ShieldAlert, Flag, Share2, Search } from "lucide-react";
import { discountLong, discountLabel, timeLeft, euro, availabilityLabel } from "@/lib/format";
import ClientAuthDialog from "@/components/ClientAuthDialog";
import ReportDialog from "@/components/ReportDialog";
import UrgencyCountdown from "@/components/UrgencyCountdown";
import StockBar from "@/components/StockBar";
import { toast } from "sonner";
import { triggerCouponFly, handleClaimError } from "@/lib/couponFly";

export default function OfferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { client, refreshClient } = useAuth();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [claimedId, setClaimedId] = useState(null);

  useEffect(() => {
    api.get(`/offers/${id}`).then((r) => setOffer(r.data)).catch(() => setOffer(null)).finally(() => setLoading(false));
  }, [id]);

  // Detect if this offer is already in the client's coupons for the CURRENT cycle,
  // so the button reflects "already downloaded" instead of inviting another request.
  const loadClaimed = useCallback(async () => {
    if (!getToken("client") || !offer) { setClaimedId(null); return; }
    try {
      const r = await api.get("/client/claims", { headers: authHeaders("client") });
      const cyc = offer.cycle || 1;
      const match = (r.data.claims || []).find((c) => c.offer_id === offer.offer_id && (c.cycle || 1) === cyc);
      setClaimedId(match ? match.claim_id : null);
    } catch (_) {}
  }, [offer]);
  useEffect(() => { loadClaimed(); }, [loadClaimed, client]);

  const claim = async (e) => {
    if (!getToken("client")) { setAuthOpen(true); return; }
    const btn = e?.currentTarget || null;
    setClaiming(true);
    try {
      const r = await api.post(`/offers/${id}/claim`, {}, { headers: authHeaders("client") });
      setClaimedId(r.data.claim_id);
      if (r.data.redeemed) {
        toast.info("Hai già utilizzato questo coupon");
        navigate(`/coupon/${r.data.claim_id}`);
      } else {
        triggerCouponFly(btn, discountLabel(offer), r.data.claim_id);
      }
    } catch (err) {
      if (!handleClaimError(err)) toast.error(err?.response?.data?.detail || "Impossibile richiedere il coupon");
    } finally {
      setClaiming(false);
    }
  };

  const toggleSave = async () => {
    if (!getToken("client")) { setAuthOpen(true); return; }
    try {
      await api.post(`/client/saved/${id}`, {}, { headers: authHeaders("client") });
      refreshClient();
    } catch (_) {}
  };

  const shareOffer = async () => {
    const url = `${window.location.origin}/offer/${id}`;
    const text = `${offer?.title || "Offerta"} su KoupoNow`;
    if (navigator.share) {
      try { await navigator.share({ title: "KoupoNow", text, url }); } catch (_) {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Link copiato negli appunti"); }
      catch (_) { toast.error("Copia non riuscita"); }
    }
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;
  if (!offer) return <div className="py-24 text-center text-slate-500">Offerta non trovata.</div>;

  const saved = new Set((client && client.saved_offer_ids) || []).has(id);
  const gallery = (offer.images && offer.images.length) ? offer.images : [];
  const img = gallery[heroIdx] || gallery[0] || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&q=75";
  const left = timeLeft(offer.expires_at);
  const availability = availabilityLabel(offer);
  const endsToday = !offer.recurring && offer.expires_at && new Date(offer.expires_at).toDateString() === new Date().toDateString();
  const showCountdown = !offer.recurring && left && left !== "Scaduta" && !endsToday;
  const nowD = new Date();
  const isExpired = !offer.recurring && offer.expires_at && new Date(offer.expires_at) <= nowD;
  const isExhausted = !!offer.quantity && offer.remaining !== null && offer.remaining !== undefined && offer.remaining <= 0;
  const isSuspended = offer.active === false || offer.archived === true;
  const invalid = isExpired || isExhausted || isSuspended;
  const invalidReason = isExhausted
    ? "Questa offerta è esaurita: tutti i coupon disponibili sono stati richiesti."
    : isSuspended
    ? "Questa offerta non è più disponibile."
    : "Questa offerta è scaduta e non è più valida.";

  return (
    <div className="kn-fade-up -mx-4 -my-4">
      <div className="relative h-64 sm:h-80">
        <img src={img} alt={offer.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=900&q=75"; }} />
        <div className="absolute inset-0 kn-hero-scrim" />
        <button onClick={() => navigate(-1)} data-testid="offer-back" className="absolute top-4 left-4 h-10 w-10 rounded-full bg-white/90 flex items-center justify-center shadow">
          <ArrowLeft className="h-5 w-5 text-slate-700" />
        </button>
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button onClick={shareOffer} data-testid="offer-share" className="h-10 w-10 rounded-full bg-white/90 flex items-center justify-center shadow">
            <Share2 className="h-5 w-5 text-slate-600" />
          </button>
          <button onClick={toggleSave} data-testid="offer-detail-save" className="h-10 w-10 rounded-full bg-white/90 flex items-center justify-center shadow">
            <Heart className={`h-5 w-5 ${saved ? "fill-[#E63946] text-[#E63946]" : "text-slate-600"}`} />
          </button>
        </div>
      </div>

      {gallery.length > 1 && (
        <div className="flex gap-2 px-4 pt-3 overflow-x-auto" data-testid="offer-gallery">
          {gallery.map((g, i) => (
            <button key={i} onClick={() => setHeroIdx(i)} data-testid={`offer-thumb-${i}`}
              className={`h-16 w-16 rounded-xl overflow-hidden shrink-0 border-2 ${i === heroIdx ? "border-[#E63946]" : "border-transparent"}`}>
              <img src={g} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="px-4 py-5 space-y-4">
        {invalid && (
          <div data-testid="offer-invalid-banner" className="rounded-2xl border border-red-200 bg-red-50 p-4 space-y-2.5">
            <div className="flex items-start gap-2.5">
              <ShieldAlert className="h-5 w-5 text-[#DC2626] shrink-0 mt-0.5" />
              <div>
                <p className="font-extrabold text-[#B91C1C]">Coupon non più valido</p>
                <p className="text-sm text-red-700/90 mt-0.5">{invalidReason}</p>
              </div>
            </div>
            <Button onClick={() => navigate(`/?cat=${offer.category}`)} data-testid="find-similar-btn"
              className="w-full h-11 rounded-xl font-bold gap-2">
              <Search className="h-4 w-4" /> Cerca offerte simili
            </Button>
          </div>
        )}
        <button onClick={() => navigate(`/store/${offer.vendor_id}`)} data-testid="offer-store-link" className="flex items-center gap-2 text-slate-500 hover:text-[#E63946] transition-colors">
          {offer.online ? <Globe className="h-4 w-4" /> : <Store className="h-4 w-4" />}
          <span className="font-semibold underline underline-offset-2">{offer.store_name}</span>
        </button>

        <h1 className="text-2xl font-extrabold text-slate-900 leading-tight">{offer.title}</h1>

        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFE4E6] text-[#BE123C] text-sm font-semibold">
            <Tag className="h-3.5 w-3.5" /> {discountLong(offer)}
          </span>
          {offer.category_label && (
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-semibold">{offer.category_label}</span>
          )}
          {showCountdown && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-sm font-semibold">
              <Clock className="h-3.5 w-3.5" /> Scade tra {left}
            </span>
          )}
        </div>

        {availability && (
          <div data-testid="offer-availability" className="flex items-center gap-2 p-3 rounded-xl bg-[#FFE4E6] text-[#BE123C] text-sm font-bold">
            <Clock className="h-4 w-4 shrink-0" /> {availability}
          </div>
        )}

        <UrgencyCountdown expiresAt={offer.expires_at} recurring={offer.recurring} variant="detail" />

        {offer.min_spend > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 text-amber-700 text-sm font-medium">
            <Info className="h-4 w-4 shrink-0" /> Spesa minima {euro(offer.min_spend)}
          </div>
        )}

        {offer.description && <p className="text-slate-600 leading-relaxed">{offer.description}</p>}
        {offer.notes && (
          <div className="p-3 rounded-xl bg-slate-50 text-slate-500 text-sm">
            <span className="font-semibold text-slate-600">Condizioni: </span>{offer.notes}
          </div>
        )}
        {offer.remaining !== null && offer.remaining !== undefined && offer.quantity && (
          <StockBar quantity={offer.quantity} remaining={offer.remaining} variant="detail" />
        )}

        <div className="rounded-xl bg-slate-50 p-3 flex items-start gap-2 text-slate-500 text-sm">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
          {offer.online
            ? "Riceverai un codice sconto da usare sul sito del negozio."
            : "Riceverai un QR + codice a rotazione da mostrare in cassa. Non condividerlo: cambia ogni 30 secondi."}
        </div>

        <button onClick={() => setReportOpen(true)} data-testid="report-offer-btn" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E63946] font-semibold">
          <Flag className="h-4 w-4" /> Segnala questa offerta
        </button>
      </div>

      <div className="sticky bottom-16 px-4 pb-4 pt-2 bg-gradient-to-t from-white via-white to-transparent">
        {invalid ? (
          <Button onClick={() => navigate(`/?cat=${offer.category}`)} data-testid="find-similar-cta"
            variant="outline" className="w-full h-14 text-lg font-extrabold rounded-2xl gap-2">
            <Search className="h-5 w-5" /> Cerca offerte simili
          </Button>
        ) : claimedId ? (
          <Button onClick={() => navigate(`/coupon/${claimedId}`)} data-testid="claimed-offer-btn"
            className="w-full h-14 text-lg font-extrabold rounded-2xl gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
            <CheckCircle2 className="h-5 w-5" /> Già scaricato · Vai al coupon
          </Button>
        ) : (
          <Button onClick={claim} disabled={claiming} data-testid="claim-offer-btn" className="w-full h-14 text-lg font-extrabold rounded-2xl gap-2">
            {claiming ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
            Richiedi coupon
          </Button>
        )}
      </div>

      <ClientAuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => claim()} />
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} vendorId={offer.vendor_id} offerId={offer.offer_id} title="Segnala questa offerta" />
    </div>
  );
}
